import { useEffect, useState, useContext, useMemo, useRef } from "react";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { callFunction } from "../api/callFunction";
import { db } from "../firebase";
import { GlobalContext } from "../context/GlobalContext";
import { toMillis, formatRelativeTime, MessageBubble } from "../components/MessageThread";

export default function Conversations() {
  const { globalState } = useContext(GlobalContext);
  const isSuperAdmin = globalState.isSuperAdmin;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [conversations, setConversations] = useState([]); // [{ userId, user, lastMessage, lastMs, lastType, lastDirection, count, relays }]
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [attentionOnly, setAttentionOnly] = useState(false);

  // Selected thread (live).
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const threadContainerRef = useRef(null);

  // --- Build the inbox via the super-admin callable. The collection-group read
  // over all `messages` and the (client-private) `smsRelays` join happen server
  // side with the Admin SDK, so security rules stay locked down. ---
  useEffect(() => {
    if (!isSuperAdmin) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const data = await callFunction("adminListConversations", undefined, { timeout: 120000 });
        const convos = (data?.conversations || []).sort(
          (a, b) => b.lastMs - a.lastMs
        );
        if (!cancelled) {
          setConversations(convos);
          setLoading(false);
        }
      } catch (err) {
        console.error("Error loading conversations:", err);
        if (!cancelled) {
          setLoadError(
            err?.message || "Failed to load conversations."
          );
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  // --- Live thread for the selected conversation. ---
  useEffect(() => {
    if (!selectedUserId) {
      setThreadMessages([]);
      return;
    }
    setThreadLoading(true);
    const q = query(
      collection(db, "users", selectedUserId, "messages"),
      orderBy("timestamp", "asc")
    );
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const msgs = snap.docs.map((d) => {
          const data = d.data();
          return { id: d.id, ...data, _ms: toMillis(data.timestamp) };
        });
        setThreadMessages(msgs);
        setThreadLoading(false);
        setTimeout(() => {
          if (threadContainerRef.current) {
            threadContainerRef.current.scrollTop =
              threadContainerRef.current.scrollHeight;
          }
        }, 50);
      },
      (err) => {
        console.error("Error loading thread:", err);
        setThreadLoading(false);
      }
    );
    return () => unsubscribe();
  }, [selectedUserId]);

  const displayName = (convo) =>
    convo.name || convo.phone || `User ${convo.userId.slice(0, 6)}`;

  // A thread needs attention when a relay is open or a member escalation was
  // dropped (forwarded:false with no human reply since).
  const needsAttention = (c) => c.relays.length > 0 || (c.unroutedCount || 0) > 0;

  const filteredConversations = useMemo(() => {
    let list = conversations;
    if (attentionOnly) list = list.filter(needsAttention);
    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (c) =>
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.phone && c.phone.toLowerCase().includes(q)) ||
        c.userId.toLowerCase().includes(q) ||
        (c.lastMessage && c.lastMessage.toLowerCase().includes(q))
    );
  }, [conversations, searchQuery, attentionOnly]);

  const selectedConvo = useMemo(
    () => conversations.find((c) => c.userId === selectedUserId) || null,
    [conversations, selectedUserId]
  );

  const attentionCount = useMemo(
    () => conversations.filter(needsAttention).length,
    [conversations]
  );

  if (!isSuperAdmin) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 mt-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">SMS Agent</h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          This view is only available to super admins.
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mt-4 sm:mt-0">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">SMS Agent</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Every member's conversation with the AI assistant, most recent first.
          {conversations.length > 0 && (
            <span> Showing {conversations.length} active threads.</span>
          )}
        </p>
      </div>

      <div className="mt-4 flex flex-col lg:flex-row gap-4 h-[calc(100vh-180px)]">
        {/* Inbox list — full-screen on mobile, hidden once a thread is open. */}
        <div
          className={`${
            selectedUserId ? "hidden lg:flex" : "flex"
          } lg:w-80 flex-shrink-0 flex-col border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden`}
        >
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 space-y-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search name, phone, or message…"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <button
              onClick={() => setAttentionOnly((v) => !v)}
              className={`w-full px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                attentionOnly
                  ? "bg-amber-500 text-white"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              }`}
            >
              {attentionOnly ? "Showing needs attention" : "Needs attention"}
              {attentionCount > 0 && ` (${attentionCount})`}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : loadError ? (
              <div className="p-4 text-sm text-red-600 dark:text-red-400">{loadError}</div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-4 text-sm text-gray-400 dark:text-gray-500 text-center">
                No conversations found.
              </div>
            ) : (
              filteredConversations.map((convo) => {
                const isSelected = convo.userId === selectedUserId;
                const escalated = convo.relays.length > 0;
                return (
                  <button
                    key={convo.userId}
                    onClick={() => setSelectedUserId(convo.userId)}
                    className={`w-full text-left px-3 py-3 border-b border-gray-100 dark:border-gray-800 transition-colors ${
                      isSelected ? "bg-primary/10" : "hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {displayName(convo)}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                        {formatRelativeTime(convo.lastMs)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {(convo.unroutedCount || 0) > 0 && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                          missed reply
                          {convo.unroutedCount > 1 && ` ×${convo.unroutedCount}`}
                        </span>
                      )}
                      {escalated && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                          {convo.relays.map((r) => r.counterpart).join("/")}
                        </span>
                      )}
                      <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {convo.lastDirection === "outbound" ? "↩ " : ""}
                        {convo.lastMessage || "(no text)"}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Thread pane — hidden on mobile until a conversation is picked. */}
        <div
          className={`${
            selectedUserId ? "flex" : "hidden lg:flex"
          } flex-1 flex-col border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden min-h-0`}
        >
          {!selectedConvo ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
              Select a conversation to view it.
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                <button
                  onClick={() => setSelectedUserId(null)}
                  aria-label="Back to conversations"
                  className="lg:hidden -ml-2 p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {displayName(selectedConvo)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {selectedConvo.phone || ""}
                    {selectedConvo.phone ? " · " : ""}
                    {selectedConvo.userId}
                  </div>
                </div>
                {(selectedConvo.relays.length > 0 ||
                  (selectedConvo.unroutedCount || 0) > 0) && (
                  <div className="flex gap-1 flex-shrink-0">
                    {(selectedConvo.unroutedCount || 0) > 0 && (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                        title="Member reply was never forwarded to a human. Texting the member's thread number won't work without a relay - message them via the Admin Agent to clear this."
                      >
                        {selectedConvo.unroutedCount} missed{" "}
                        {selectedConvo.unroutedCount === 1 ? "reply" : "replies"}
                      </span>
                    )}
                    {selectedConvo.relays.map((r, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                        title={r.topic || ""}
                      >
                        {r.counterpart} relay open
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div
                ref={threadContainerRef}
                className="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-800 space-y-3"
              >
                {threadLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                  </div>
                ) : threadMessages.length === 0 ? (
                  <div className="text-center text-gray-400 dark:text-gray-500 text-sm">
                    No messages.
                  </div>
                ) : (
                  threadMessages.map((msg) => (
                    <MessageBubble key={msg.id} msg={msg} />
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
